// Servicio genérico para llamadas a la API
class ApiService {
  constructor() {
    this.baseURL = process.env.REACT_APP_API_BASE_URL || 'https://localhost:7156/api';
  }

  // Método genérico para hacer peticiones HTTP
  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    
    const skipAuth = options.skipAuth === true;
    const authTokenOverride = options.authTokenOverride || null;
    
    // Debug: verificar que getAuthHeaders funcione
    const authHeaders = authTokenOverride
      ? { Authorization: `Bearer ${authTokenOverride}` }
      : (skipAuth ? {} : this.getAuthHeaders());
    
    // Construir headers correctamente
    const finalHeaders = {
      'Content-Type': 'application/json; charset=utf-8',
      'Accept': 'application/json, text/plain, */*',
      'Cache-Control': 'no-cache',
      ...options.headers, // Headers específicos de la petición primero
      ...authHeaders, // Headers de autorización al final (tienen prioridad)
    };
    
    const config = {
      ...options,
      headers: finalHeaders, // Asegurar que los headers no se sobrescriban
    };
    delete config.skipAuth;
    delete config.authTokenOverride;
    
    // Debug: verificar headers finales

    // Si hay datos en el body, convertir a JSON (excepto FormData y URLSearchParams)
    if (config.body && typeof config.body === 'object' && 
        !(config.body instanceof FormData) && 
        !(config.body instanceof URLSearchParams)) {
      config.body = JSON.stringify(config.body);
    }
    
    // Si el body es FormData, remover el Content-Type para que el navegador lo configure automáticamente
    if (config.body instanceof FormData) {
      delete config.headers['Content-Type'];
    }

    try {
      
      const response = await fetch(url, config);
      


      // Si la respuesta no es exitosa, lanzar error
      if (!response.ok) {
        const responseClone = response.clone();
        let rawText = '';
        try {
          rawText = await responseClone.text();
        } catch (e) {
          rawText = '';
        }

        let errorData = {};
        try {
          errorData = await response.json();
        } catch (e) {
          // Si no se puede parsear como JSON, intentar parsear el texto bruto
          try {
            errorData = rawText ? JSON.parse(rawText) : {};
          } catch (parseError) {
            errorData = {};
          }
        }
        
        // Extraer mensaje de error del servidor
        const getServerMessage = (payload = {}) => {
          if (!payload) return '';

          if (typeof payload === 'string') return payload;
          const knownKeys = [
            'message', 'Message', 'mensaje', 'Mensaje', 'mensajes', 'Mensajes',
            'error', 'Error', 'errors', 'Errors',
            'title', 'Title', 'detail', 'Detail',
            'errorMessage', 'ErrorMessage',
            'description', 'Description'
          ];
          for (const key of knownKeys) {
            if (payload[key]) return payload[key];
          }

          // Si viene errors como objeto { campo: [mensajes] }
          if (payload.errors && typeof payload.errors === 'object' && !Array.isArray(payload.errors)) {
            const firstKey = Object.keys(payload.errors)[0];
            const firstVal = payload.errors[firstKey];
            if (Array.isArray(firstVal) && firstVal.length > 0) {
              return firstVal[0];
            }
          }

          // Si viene errors como array de strings
          if (Array.isArray(payload.errors) && payload.errors.length > 0) {
            return payload.errors[0];
          }

          // Si vienen mensajes como array
          if (Array.isArray(payload.mensajes) && payload.mensajes.length > 0) {
            return payload.mensajes[0];
          }

          // Fallback: primer valor string del objeto
          const firstValue = Object.values(payload).find((v) => typeof v === 'string');
          if (firstValue) return firstValue;

          // Último recurso: stringify
          try {
            return JSON.stringify(payload);
          } catch (e) {
            return '';
          }
        };

        const trimmedText = (rawText || '').trim();

        let serverMessage = getServerMessage(errorData);

        // Si no hay mensaje útil, usar el texto crudo
        if (!serverMessage || serverMessage === '{}' || serverMessage === '[]') {
          if (trimmedText && trimmedText !== '{}' && trimmedText !== '[]') {
            serverMessage = trimmedText;
          }
        }

        // Fallback final
        if (!serverMessage) {
          serverMessage = `HTTP Error: ${response.status} ${response.statusText || ''}`.trim();
        }

        // Log detallado del error para debugging
        console.error(`%c❌ API ERROR DETAILS:`, 'color: #ef4444; font-weight: bold;');
        console.error(`%c│ Status: ${response.status}`, 'color: #ef4444;');
        console.error(`%c│ Status Text: ${response.statusText}`, 'color: #ef4444;');
        console.error(`%c│ URL: ${url}`, 'color: #ef4444;');
        console.error(`%c│ Method: ${config.method || 'GET'}`, 'color: #ef4444;');
        console.error(`%c│ Headers:`, 'color: #ef4444;', config.headers);
        console.error(`%c│ Body:`, 'color: #ef4444;', config.body);
        console.error(`%c│ Error Data:`, 'color: #ef4444;', errorData);

        // Manejo especial para errores 401 (No Autorizado)
        if (response.status === 401 && !skipAuth) {
          console.warn(`%c🔐 ERROR 401: Usuario no autorizado`, 'color: #f59e0b; font-weight: bold;');
          
          // Limpiar cookies de autenticación
          this.clearAuthCookies();

          // Avisar a la app (por ejemplo, para cortar la conexión de presencia en línea)
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('auth:unauthorized'));
          }
          
          // No forzar redirección aquí para evitar conflictos
          // La redirección la manejará el AuthContext y ProtectedRoute
          
          throw new Error('Sesión expirada. Por favor, inicia sesión nuevamente.');
        }
        
        throw new Error(serverMessage || `HTTP Error: ${response.status} ${response.statusText}`);
      }

      // Intentar parsear como JSON
      const data = await response.json();
      
      // Verificar el formato de respuesta estándar del servidor
      if (data && typeof data.ok !== 'undefined') {
        if (!data.ok) {
          // El servidor devolvió ok: false, lanzar error con el mensaje del servidor
          throw new Error(data.error || 'Error del servidor');
        }
        // Si ok: true, continuar con los datos
      }
      
      return data;
      
    } catch (error) {
      console.error('API Error:', error);
      throw error;
    }
  }

  // Métodos de conveniencia para diferentes tipos de peticiones
  async get(endpoint, headers = {}) {
    return this.request(endpoint, {
      method: 'GET',
      headers,
    });
  }

  async getPublic(endpoint, headers = {}) {
    return this.request(endpoint, {
      method: 'GET',
      headers,
      skipAuth: true
    });
  }

  async post(endpoint, data, headers = {}) {
    return this.request(endpoint, {
      method: 'POST',
      body: data,
      headers,
    });
  }

  async postPublic(endpoint, data, headers = {}) {
    return this.request(endpoint, {
      method: 'POST',
      body: data,
      headers,
      skipAuth: true
    });
  }

  // Método especializado para login que maneja automáticamente errores 415
  async postLogin(endpoint, credentials) {
    const strategies = [
      {
        name: 'JSON con charset UTF-8',
        execute: () => this.request(endpoint, {
          method: 'POST',
          body: JSON.stringify(credentials),
          headers: {
            'Content-Type': 'application/json; charset=utf-8',
            'Accept': 'application/json, text/plain, */*'
          }
        })
      },
      {
        name: 'FormData',
        execute: () => {
          const formData = new FormData();
          Object.keys(credentials).forEach(key => {
            formData.append(key, credentials[key]);
          });
          return this.request(endpoint, {
            method: 'POST',
            body: formData,
            headers: {
              'Accept': 'application/json, text/plain, */*'
            }
          });
        }
      },
      {
        name: 'URL-encoded',
        execute: () => {
          const urlEncodedData = new URLSearchParams();
          Object.keys(credentials).forEach(key => {
            urlEncodedData.append(key, credentials[key]);
          });
          return this.request(endpoint, {
            method: 'POST',
            body: urlEncodedData,
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded; charset=utf-8',
              'Accept': 'application/json, text/plain, */*'
            }
          });
        }
      },
      {
        name: 'JSON plano',
        execute: () => this.request(endpoint, {
          method: 'POST',
          body: JSON.stringify(credentials),
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          }
        })
      }
    ];

    for (let i = 0; i < strategies.length; i++) {
      const strategy = strategies[i];
      try {
        const result = await strategy.execute();
        return result;
      } catch (error) {
        if (error.message.includes('415') || error.message.includes('Unsupported Media Type')) {
          if (i === strategies.length - 1) {
            console.error('%c❌ Todas las estrategias de content-type fallaron', 'color: #ef4444; font-weight: bold;');
            throw new Error('Error 415: El servidor no acepta ninguno de los formatos de contenido probados (JSON, FormData, URL-encoded)');
          }
        } else {
          console.error(`%c❌ Estrategia ${i + 1} falló por otro motivo: ${strategy.name}`, 'color: #ef4444;');
          throw error;
        }
      }
    }
  }

  async put(endpoint, data, headers = {}) {
    return this.request(endpoint, {
      method: 'PUT',
      body: data,
      headers,
    });
  }

  async delete(endpoint, headers = {}) {
    return this.request(endpoint, {
      method: 'DELETE',
      headers,
    });
  }

  // Método para configurar token de autorización
  setAuthToken(token) {
    if (token) {
      this.authToken = token;
    } else {
      this.authToken = null;
    }
  }

  // Método para obtener headers con autorización
  getAuthHeaders() {
    // Primero intentar obtener el token del ApiService
    let token = this.authToken;
    
    if (token) {
    } else {
      // Si no hay token en el ApiService, intentar obtenerlo de las cookies
      token = this.getTokenFromCookies();
      
      // Si encontramos token en cookies, guardarlo en el ApiService para futuras peticiones
      if (token) {
        this.authToken = token;
      }
    }
    
    if (token) {
      return {
        'Authorization': `Bearer ${token}`
      };
    } else {
      return {};
    }
  }

  // Método auxiliar para obtener token de las cookies
  getTokenFromCookies() {
    try {
      
      if (!document.cookie || document.cookie.trim() === '') {
        return null;
      }
      
      const cookies = document.cookie.split(';');
      
      for (let i = 0; i < cookies.length; i++) {
        const cookie = cookies[i].trim();
        const [name, value] = cookie.split('=');
        
        
        // Primero intentar con la cookie directa del token 'auth_token'
        if (name === 'auth_token' && value) {
          const decodedToken = decodeURIComponent(value);
          return decodedToken;
        }
        
        // Segundo: intentar con 'user_data' que contiene el objeto usuario
        if (name === 'user_data' && value) {
          try {
            const decodedValue = decodeURIComponent(value);
            const userData = JSON.parse(decodedValue);
            
            if (userData.token) {
              return userData.token;
            }
          } catch (parseError) {
          }
        }
        
        // Fallback: buscar con el nombre anterior 'authUser' por compatibilidad
        if (name === 'authUser' && value) {
          try {
            const decodedValue = decodeURIComponent(value);
            const userData = JSON.parse(decodedValue);
            
            if (userData.token) {
              return userData.token;
            }
          } catch (parseError) {
          }
        }
      }
      
      return null;
      
    } catch (error) {
      console.error('%c│ 💥 ERROR CRÍTICO al buscar token:', 'color: #ef4444; font-weight: bold;', error);
      return null;
    }
  }

  // Método para limpiar cookies de autenticación
  clearAuthCookies() {
    try {
      
      // Lista de cookies que se deben limpiar
      const cookiesToClear = [
        'user_data',    // Cookie principal actual
        'auth_token',   // Token de autenticación actual
        'authUser',     // Cookie anterior (para compatibilidad)
        'authToken',    // Token anterior (para compatibilidad)
        'userSession',
        'auth',
        'userData'
      ];
      
      cookiesToClear.forEach(cookieName => {
        // Limpiar cookie paral dominio actual
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
        
        // Limpiar cookie para subdominios también
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${window.location.hostname};`;
        
      });
      
      // También limpiar el localStorage si se usa
      if (typeof localStorage !== 'undefined') {
        localStorage.removeItem('authUser');
        localStorage.removeItem('authToken');
        localStorage.removeItem('userData');
      }
      
      // Limpiar token del ApiService
      this.authToken = null;
      
      
    } catch (error) {
      console.error('Error al limpiar cookies de autenticación:', error);
    }
  }

  // Método de debug para verificar el estado del token
  debugTokenStatus() {
    
    if (this.authToken) {
    }
    
    const cookieToken = this.getTokenFromCookies();
    
    if (cookieToken && !this.authToken) {
    }
  }

  // Método de prueba directo para hacer una petición simple con logging detallado
  async testRequestWithAuth(endpoint = '/DashboardProfesor/estadisticas') {
    
    // 1. Debug inicial
    this.debugTokenStatus();
    
    // 2. Construir headers manualmente y mostrarlos
    const authHeaders = this.getAuthHeaders();
    
    const finalHeaders = {
      'Content-Type': 'application/json; charset=utf-8',
      'Accept': 'application/json, text/plain, */*',
      'Cache-Control': 'no-cache',
      ...authHeaders,
    };
    

    
    // 3. Hacer petición directa con fetch
    const url = `${this.baseURL}${endpoint}`;
    
    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: finalHeaders,
      });
      
      
      if (response.ok) {
        const data = await response.json();
        return data;
      } else {
        throw new Error(`${response.status} ${response.statusText}`);
      }
    } catch (error) {
      throw error;
    } finally {
    }
  }
}

// Crear instancia singleton
const apiService = new ApiService();

export default apiService;
